import React, { useState, useEffect } from 'react';
import { Editor } from '@hugerte/hugerte-react';

interface Attachment {
  id?: number;
  filename: string;
  size: number;
  type: string;
}

interface EmailData {
  id?: number;
  subject: string;
  body: string;
  attachments: Attachment[];
  status?: string;
}

interface EditMailFormProps {
  emailId: number;
  accessToken?: string;
  onClose: () => void;
  onSaved?: () => void;
}

const EditMailForm: React.FC<EditMailFormProps> = ({ emailId, accessToken, onClose, onSaved }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`/api/email/${emailId}`, {
      headers: accessToken ? { 'Authorization': `Bearer ${accessToken}` } : undefined
    })
      .then(res => res.json())
      .then((data: EmailData) => {
        setSubject(data.subject);
        setBody(data.body);
        setAttachments(data.attachments || []);
      })
      .finally(() => setLoading(false));
  }, [emailId, accessToken]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`/api/email/${emailId}/attachment`, {
      method: 'POST',
      headers: accessToken ? { 'Authorization': `Bearer ${accessToken}` } : undefined,
      body: formData,
    });
    if (res.ok) {
      const newAttachment = await res.json();
      setAttachments([...attachments, newAttachment]);
      setFile(null);
    } else {
      alert('Failed to upload attachment');
    }
  };

  const handleRemoveAttachment = async (att: Attachment) => {
    if (!att.id) {
      setAttachments(attachments.filter(a => a !== att));
      return;
    }
    const res = await fetch(`/api/email/${emailId}/attachment/${att.id}`, {
      method: 'DELETE',
      headers: accessToken ? { 'Authorization': `Bearer ${accessToken}` } : undefined,
    });
    if (res.ok) {
      setAttachments(attachments.filter(a => a.id !== att.id));
    } else {
      alert('Failed to remove attachment');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const payload = { subject, body, attachments };
    const res = await fetch(`/api/email/${emailId}` , {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {})
      },
      body: JSON.stringify(payload),
    });
    setLoading(false);
    if (res.ok) {
      alert('Email saved.');
      if (onSaved) onSaved();
      onClose();
    } else {
      alert('Failed to save email');
    }
  };

  return (
    <div className="p-8 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Edit Mail</h1>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="mb-4">
          <label className="block font-semibold mb-2">Subject</label>
          <input
            type="text"
            className="w-full border px-3 py-2 rounded"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            required
          />
        </div>
        <div className="mb-4">
          <label className="block font-semibold mb-2">Body</label>
          <Editor
            value={body}
            onEditorChange={setBody}
            init={{ height: 300 }}
          />
        </div>
        <div className="mb-4">
          <label className="block font-semibold mb-2">Attachment (PDF)</label>
          <input type="file" accept="application/pdf" onChange={handleFileChange} />
          <button type="button" className="ml-2 px-3 py-1 bg-gray-200 rounded" onClick={handleUpload} disabled={!file || loading}>
            Upload
          </button>
          {file && <div className="mt-2 text-sm">Selected: {file.name}</div>}
          <ul className="mt-2">
            {attachments.map(att => (
              <li key={att.id || att.filename} className="flex items-center justify-between">
                <span>{att.filename}</span>
                <button type="button" className="text-red-600 ml-2" onClick={() => handleRemoveAttachment(att)}>
                  Remove
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex gap-4">
          <button
            type="submit"
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            disabled={loading}
          >
            Save Changes
          </button>
          <button
            type="button"
            className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditMailForm;
